import { Accordion as FukkuAccordion } from 'fukku/Accordion'
import { FilterType } from 'product-list/types/Filters'
import {
	type ComponentProps,
	type Dispatch,
	type SetStateAction,
	useMemo,
} from 'react'

interface AccordionProps {
	readonly idSelector: string
	readonly label: string
	readonly type: FilterType
	readonly expandedId: string
	readonly setExpandedId: Dispatch<SetStateAction<string>>
	readonly children: React.ReactNode
	readonly options?: { id: string; label: string }[]
	readonly selection?: string[]
	readonly dataTestId?: string
}

type AccordionData = ComponentProps<typeof FukkuAccordion>['data']

function getSubtitle(
	options: { id: string; label: string }[] | undefined,
	selection: string[] | undefined,
	type: FilterType
) {
	if (!options && !selection) {
		return ''
	}

	if (
		(!options?.length && selection) ||
		(FilterType.price === type && selection?.length)
	) {
		return selection[0]
	}

	return (
		options &&
		selection &&
		options
			.filter((option) => selection.includes(option.id))
			.map((option) => option.label)
			.join(', ')
	)
}
export function Accordion({
	idSelector,
	label,
	type,
	expandedId,
	setExpandedId,
	children,
	options,
	selection,
	dataTestId,
}: AccordionProps): React.ReactNode {
	const showExpanded = expandedId === idSelector
	const initialSelection = useMemo(() => {
		return selection?.length && !showExpanded
			? getSubtitle(options, selection, type)
			: ''
	}, [options, selection, showExpanded, type])

	const accordionData: AccordionData = useMemo(
		() => [
			{
				showExpanded,
				shouldScroll: false,
				title: label,
				subtitle: initialSelection,
				onCollapsedStart: (_id) => {
					setExpandedId((prev: string) => (prev === idSelector ? '' : prev))
				},
				onExpandedStart: (_id) => {
					setExpandedId(idSelector)
				},
				content: <div key={idSelector}>{children}</div>,
			},
		],
		[idSelector, label, initialSelection, showExpanded, setExpandedId, children]
	)

	return (
		<div key={idSelector} data-testid={dataTestId}>
			<FukkuAccordion
				id={idSelector}
				shouldRenderOnExpand
				data={accordionData}
			/>
		</div>
	)
}
